import {Service} from '../Service';
import {upsertInto} from '../../../common/helpers/db/upsertInto';
import {RxBigRock} from "../../../common/model/db/types/RxBigRock";
import {asRxRole, isRxRole, RxRole} from "../../../common/model/db/types/RxRole";
import {asString} from "../../../common/helpers/converters/asString";
import {BigRock, Property} from "../../rest-client/aat";
import {asRxPropertyArray} from "../../../common/model/db/types/RxProperty";
import {UpsertMode} from "../../../common/helpers/db/UpsertMode";
import {RxGroupProperty} from "../../../common/model/db/types/RxGroupProperty";
import {asJsonObjectOrNull} from "../../../common/helpers/converters/asJsonObjectOrNull";

export class BigRocksService extends Service {
    async getBigRocks(groupId: number = Number.POSITIVE_INFINITY): Promise<void> {
        if (isNaN(groupId)) {
            return;
        }

        const bigRocksApi = await this.services.aat.BigRocks;
        const bigRocks = groupId === Number.POSITIVE_INFINITY
            ? await bigRocksApi.getBigRocks()
            : await bigRocksApi.getBigRocksByGroupId({id: groupId});

        this.checkStatus(bigRocks);

        await this.saveBigRocksToDB(bigRocks.bigRocks, groupId);
    }

    private async saveBigRocksToDB(bigRocks: BigRock[], groupId: number): Promise<void> {
        await Promise.all([
            ...bigRocks.map(async (it: BigRock) => {
                const rolesData: RxRole[] = [
                    asRxRole(it.roleOn),
                    asRxRole(it.roleOff)
                ].filter(isRxRole);

                await upsertInto(this.db.roles, rolesData);
            }),
            ...bigRocks.map(async (it: BigRock) => {
                const rxProps = asRxPropertyArray(
                    it.properties?.map((it: Property) => {
                            return {
                                id: it.id,
                                name: it.name,
                                description: it.description,
                                possibleValues: it.possibleValues
                            }
                        }
                    ) ?? []
                );

                await upsertInto(
                    this.db.properties,
                    rxProps,
                    UpsertMode.ADD_OVERWRITE
                );
            }),
            ...bigRocks.map(async (it: BigRock) => {
                const roleId = it.groupRockRole?.id;
                const rxGroupProps = it.propertiesSet?.map<RxGroupProperty>((it: Property) => {
                        return {
                            id: `${groupId}_${roleId}_${it.id}`,
                            group: asString(groupId),
                            property: asString(it.id),
                            override_value: it.overrideValue,
                            possible_values: asJsonObjectOrNull(it.possibleValues) ?? undefined,
                            additionalProperties: it.additionalProperties,
                            allow_user_override: it.allowUserOverride
                        }
                    }
                ) ?? [];

                await upsertInto(
                    this.db.group_properties,
                    rxGroupProps,
                    UpsertMode.ADD_OVERWRITE
                );
            })
        ]);

        const data = bigRocks.map<RxBigRock>((it: BigRock) => {
            return {
                id: (it.name + '_' + it.destiny?.join('_')).toUpperCase(),
                name: it.name,
                role_on: it.roleOn ? asString(it.roleOn.id) : undefined,
                role_off: it.roleOff ? asString(it.roleOff.id) : undefined,
                group_rock_role: it.groupRockRole ? asString(it.groupRockRole.id) : undefined,
                destiny: it.destiny ?? [],
                enabled_group_ids: it.enabledGroupIds?.map(asString) ?? [],
                disabled_group_ids: it.disabledGroupIds?.map(asString) ?? [],
                property_ids: it.properties?.map((it: Property) => asString(it.id)) ?? [],
                sub_rock_ids: it.subRocks?.map((it: BigRock) => (it.name + '_' + it.destiny?.join('_')).toUpperCase()) ?? [],
                is_sub_rock: it.isSubRock ?? false
            }
        })

        await upsertInto(
            this.db.big_rocks,
            data,
            UpsertMode.ADD_OVERWRITE
        );

        for (const it of bigRocks) {
            await this.saveBigRocksToDB(it.subRocks ?? [], groupId);
        }
    }
}